import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { CUSTOMER_ENROLL_URL, PARTNER_ENROLL_URL } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const INTERESTS = [
  { value: "individual", label: "Individual / family plan" },
  { value: "business", label: "Small business plan" },
  { value: "partner", label: "Becoming a partner" },
];

const TIMES = ["Morning", "Afternoon", "Evening", "Weekend"];

export default function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "individual", preferred_time: "", preferred_contact: "text", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, interest: e.detail }));
    const onMessage = (e) => setForm((f) => ({ ...f, message: e.detail }));
    window.addEventListener("lead-interest", onPrefill);
    window.addEventListener("lead-message", onMessage);
    return () => {
      window.removeEventListener("lead-interest", onPrefill);
      window.removeEventListener("lead-message", onMessage);
    };
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest: form.interest,
        preferred_time: form.preferred_time || null,
        preferred_contact: form.preferred_contact,
        message: form.message.trim() || null,
        consent: true,
      });
      setDone(true);
      toast.success("Details sent — I'll be in touch soon.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0B192C] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-colors duration-200";

  return (
    <section id="get-started" className="bg-white py-24 md:py-36 scroll-mt-20" data-testid="lead-form-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="rounded-[2.5rem] bg-[#0B192C] grain-overlay relative overflow-hidden"
        >
          <div className="absolute inset-0 hero-spotlight" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-14 p-8 md:p-16">
            <div>
              <p className="font-mono-accent text-xs uppercase tracking-[0.28em] text-sky-400 mb-6">You made it to the form</p>
              <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white leading-[1.1] mb-6">
                Now that you know what it is — let's talk.
              </h2>
              <p className="text-slate-300 leading-relaxed mb-10 max-w-md">
                Tell me what you're curious about and I'll send the exact plan details, coverage
                documents, and current pricing. One conversation, zero obligation.
              </p>
              <ul className="space-y-4 text-sm text-slate-300">
                {["Exact pricing & coverage documents, not a sales script", "I reply personally — no call center", "Your info stays with me. No lists, no spam"].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <ShieldCheck size={17} className="text-sky-400 mt-0.5 shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="associate-card">
                <img src="/john-jones.png" alt="John Jones, independent associate" className="w-14 h-14 rounded-full object-cover border-2 border-sky-400/60" />
                <div>
                  <p className="font-display font-bold text-white">You'll hear from me — John.</p>
                  <p className="text-sm text-slate-400 mt-0.5">One person who knows your name, not a ticket queue.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-7 md:p-9 shadow-2xl shadow-blue-950/40" data-testid="lead-form-card">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="h-full min-h-[380px] flex flex-col items-center justify-center text-center"
                    data-testid="lead-form-success"
                  >
                    <span className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center mb-6">
                      <CheckCircle2 size={32} />
                    </span>
                    <h3 className="font-display font-bold text-2xl text-[#0B192C] mb-3">Got it, {form.name.split(" ")[0]}.</h3>
                    <p className="text-slate-600 max-w-xs leading-relaxed mb-8">
                      Your request is in. Expect a personal reply with full plan details shortly.
                    </p>
                    <a
                      data-testid="enroll-link-success"
                      href={form.interest === "partner" ? PARTNER_ENROLL_URL : CUSTOMER_ENROLL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-4 decoration-blue-300 transition-colors duration-200"
                    >
                      {form.interest === "partner"
                        ? "Ready to start? See the opportunity on my official associate page"
                        : "Already decided? Enroll on my official associate page"}
                    </a>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    exit={{ opacity: 0 }}
                    onSubmit={submit}
                    onFocusCapture={() => {
                      if (!sessionStorage.getItem("mll_form_started")) {
                        sessionStorage.setItem("mll_form_started", "1");
                        window.__track?.("form_start");
                      }
                    }}
                    className="space-y-4"
                    data-testid="lead-form"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input data-testid="lead-form-name-input" required minLength={2} placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
                      <input data-testid="lead-form-phone-input" required minLength={7} type="tel" placeholder="Phone number" value={form.phone} onChange={set("phone")} className={inputCls} />
                    </div>
                    <input data-testid="lead-form-email-input" required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} />

                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">I'm interested in</p>
                      <div className="flex flex-wrap gap-2" data-testid="lead-form-interest-group">
                        {INTERESTS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            data-testid={`lead-form-interest-${opt.value}`}
                            onClick={() => setForm((f) => ({ ...f, interest: opt.value }))}
                            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
                              form.interest === opt.value
                                ? "bg-[#1D4ED8] border-[#1D4ED8] text-white"
                                : "border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <select
                      data-testid="lead-form-time-select"
                      value={form.preferred_time}
                      onChange={set("preferred_time")}
                      className={`${inputCls} text-slate-600`}
                    >
                      <option value="">Best time to reach you (optional)</option>
                      {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <textarea
                      data-testid="lead-form-message-input"
                      placeholder="Anything specific on your mind? (optional)"
                      value={form.message}
                      onChange={set("message")}
                      rows={3}
                      className={`${inputCls} resize-none`}
                    />

                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Preferred contact method</p>
                      <div className="grid grid-cols-2 gap-2" data-testid="lead-form-contact-group">
                        {[{ v: "text", label: "Text me" }, { v: "call", label: "Call me" }].map((o) => (
                          <button
                            key={o.v}
                            type="button"
                            data-testid={`lead-form-contact-${o.v}`}
                            onClick={() => setForm((f) => ({ ...f, preferred_contact: o.v }))}
                            className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors duration-200 ${
                              form.preferred_contact === o.v
                                ? "bg-[#1D4ED8] border-[#1D4ED8] text-white"
                                : "border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700"
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <label className="flex items-start gap-3 text-[11px] text-slate-500 leading-relaxed cursor-pointer select-none" data-testid="lead-form-consent-label">
                      <input data-testid="lead-form-consent-checkbox" required type="checkbox" className="mt-0.5 w-4 h-4 shrink-0 rounded border-slate-300 accent-blue-600" />
                      <span>I agree that John Jones / Elite Associates may contact me by my selected method about the plans or business opportunity I requested. Consent isn’t a condition of purchase. For texts, message and data rates may apply; reply STOP to opt out.</span>
                    </label>

                    <button
                      data-testid="lead-form-submit-button"
                      type="submit"
                      disabled={submitting}
                      className="group w-full flex items-center justify-center gap-2 bg-[#1D4ED8] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-4 rounded-xl transition-colors duration-300"
                    >
                      {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
                      {submitting ? "Sending..." : "Send me the details"}
                    </button>
                    <p className="text-[11px] text-slate-400 text-center">No spam, no obligation. Unsubscribe anytime.</p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
